// The console's live-push provider: one realtime socket per organisation, shared by every venue
// screen under it (Floor, Kitchen, Sessions). It authenticates with a member realtime token
// (`POST /v1/realtime/token`, spec/09 §1), and hands screens a way to subscribe to a venue channel
// and to watch the connection's status so they can fall back to polling when it is not live.
//
// Motion is a choice (DESIGN.md "Motion"): a live-updating floor is motion, so a member who prefers
// reduced motion gets the still, polled picture instead — the socket is never opened. A disconnected
// socket falls back the same way, because the screens read the status and poll while it is `offline`.

import type { Channel } from '@clavian/contracts/realtime';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useOptionalSurface } from '../surface';
import {
  type ChannelListener,
  createRealtimeConnection,
  type RealtimeConnection,
  type RealtimeStatus,
} from './connection';

const RealtimeContext = createContext<RealtimeConnection | null>(null);

/**
 * The gateway URL (no query string, spec/09 §1): `VITE_CLAVIAN_REALTIME_URL` — which a hosted build sets to its
 * deployment's `wss://rt.{domain}/v1/ws` (vite/pages.ts) — or else `ws(s)://<api or own origin>/v1/ws`, which only
 * a local dev server answers (it forwards `/v1/ws` to the local gateway, vite.config.ts).
 */
export function resolveRealtimeUrl(): string {
  const explicit = import.meta.env.VITE_CLAVIAN_REALTIME_URL;
  if (explicit !== undefined && explicit !== '') return explicit;
  const apiUrl = import.meta.env.VITE_CLAVIAN_API_URL;
  const base = apiUrl !== undefined && apiUrl !== '' ? apiUrl : window.location.origin;
  const url = new URL('/v1/ws', base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/** Whether the device asks for reduced motion; live push is motion, so it is off when this is true. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function RealtimeProvider({ organizationId, children }: { readonly organizationId: string; readonly children: ReactNode }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const reducedMotion = usePrefersReducedMotion();
  // The demo build opens no socket (spec/24 §4.2): its venue is simulated in the page, and the screens poll.
  const surfaceAllowsRealtime = useOptionalSurface()?.realtime !== false;
  const enabled = surfaceAllowsRealtime && !reducedMotion && typeof WebSocket !== 'undefined' && typeof window !== 'undefined';

  // Built during render (no I/O until `start`), so context has it at once and the effect only runs
  // its lifecycle — no state to set in the effect.
  const connection = useMemo<RealtimeConnection | null>(() => {
    if (!enabled) return null;
    return createRealtimeConnection({
      url: resolveRealtimeUrl(),
      fetchToken: async () => {
        const token = await api.createRealtimeToken({ body: { organizationId } });
        return { token: token.token, expiresAt: token.expiresAt };
      },
      // The console refetches its own access on this frame and re-subscribes on a fresh token (spec/09 §3).
      onAccessChanged: () => void queryClient.invalidateQueries({ queryKey: ['me', 'access'] }),
      // Banned or removed: drop everything the cache holds so the next request re-authenticates.
      onRevoked: () => queryClient.clear(),
    });
  }, [enabled, organizationId, api, queryClient]);

  useEffect(() => {
    if (connection === null) return undefined;
    connection.start();
    return () => connection.stop();
  }, [connection]);

  return <RealtimeContext.Provider value={connection}>{children}</RealtimeContext.Provider>;
}

/** Hand a ready-made connection to the screens below — a test's fake socket, so its frames reach the hooks. */
export function RealtimeConnectionProvider({ connection, children }: { readonly connection: RealtimeConnection | null; readonly children: ReactNode }): ReactNode {
  return <RealtimeContext.Provider value={connection}>{children}</RealtimeContext.Provider>;
}

/** The live socket, or `null` when the provider is not connecting (reduced motion, or no provider). */
export function useRealtimeConnection(): RealtimeConnection | null {
  return useContext(RealtimeContext);
}

/** The current connection status; `offline` whenever there is no live socket, so screens poll. */
export function useRealtimeStatus(): RealtimeStatus {
  const connection = useContext(RealtimeContext);
  return useSyncExternalStore(
    (onStoreChange) => (connection === null ? () => undefined : connection.watch(() => onStoreChange())),
    () => connection?.status ?? 'offline',
  );
}

/**
 * How long a screen waits without a live socket before it stops saying "Connecting" and says live updates are paused,
 * with a retry. Two backoff windows and then some (spec/09 §8): a blip reconnects well inside it, so only a real
 * outage — the gateway down, or a network or policy that blocks the socket — reaches it.
 */
export const REALTIME_UNAVAILABLE_AFTER_MS = 30_000;

/**
 * True once the socket has been anything but `live` for `afterMs` straight — since the screen mounted, or since it was
 * last live. Always false when no socket is wanted (no provider, reduced motion, the demo): there is nothing to wait for.
 */
export function useRealtimeUnavailable(afterMs: number = REALTIME_UNAVAILABLE_AFTER_MS): boolean {
  const connection = useContext(RealtimeContext);
  const status = useRealtimeStatus();
  const waiting = connection !== null && status !== 'live';
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => setUnavailable(true), afterMs);
    // The wait ends (live again, or unmounted): the next drop starts counting from nothing.
    return () => {
      clearTimeout(timer);
      setUnavailable(false);
    };
  }, [waiting, afterMs]);
  return waiting && unavailable;
}

/** True when a live socket is carrying push; screens turn off interval polling while it is. */
export function useRealtimeLive(): boolean {
  return useRealtimeStatus() === 'live';
}

/**
 * Subscribe to one channel's events while this component is mounted (and `enabled`). The socket
 * defers the actual `channel.subscribe` until it is authenticated and sends it again on every
 * reconnect, so a screen calls this once and lets the transport keep the subscription alive.
 */
export function useChannelEvents(channel: Channel, onEvent: ChannelListener, enabled = true): void {
  const connection = useContext(RealtimeContext);
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    if (connection === null || !enabled) return undefined;
    return connection.subscribe(channel, (envelope) => handler.current(envelope));
  }, [connection, channel, enabled]);
}
